// Shows submitted feedback on the read-only Client Feedback page.

import { format } from "date-fns";
import { Check, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MarkdownContent } from "@/components/markdown-content";
import { VerdictBadge } from "@/modules/sentinel/verdict-badge";
import type { SentinelReasonCode, SentinelReviewSummary } from "@/services/sentinelReview.service";

// Labels current and retired review reason codes.
const REASON_LABELS: Record<SentinelReasonCode, string> = {
  resolution: "Resolution",
  completeness: "Completeness",
  facts: "Facts",
  brand_tone_style: "Brand Tone & Style",
  sentiment: "Sentiment",
  tone_was_off: "Tone was off",
  factually_incorrect: "Factually incorrect",
  too_verbose: "Too verbose",
  missed_the_point: "Missed the point",
  other: "Other",
};

interface SentinelFeedbackViewProps {
  review: SentinelReviewSummary | null;
  isBusy: boolean;
  /** Omitted when the viewer may read but not act — the toggle is hidden. */
  onToggleAddressed?: (addressed: boolean) => void;
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return format(date, "MMM d, yyyy · h:mm a");
}

export function SentinelFeedbackView({ review, isBusy, onToggleAddressed }: SentinelFeedbackViewProps) {
  return (
    <div className="flex h-full w-full flex-col border-l border-border bg-background">
      <div className="flex h-16 shrink-0 items-center border-b border-border px-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Feedback</h2>
          <p className="text-xs text-muted-foreground">Client's review of this response</p>
        </div>
      </div>

      {!review ? (
        <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-muted-foreground">
          No feedback on this response yet.
        </div>
      ) : (
        <>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
            <div className="flex flex-col gap-4">
              <section className="rounded-xl border border-border p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Verdict</p>
                <div className="mt-2 flex items-center gap-2">
                  <VerdictBadge verdict={review.verdict} />
                  <span className="text-sm text-foreground">
                    {review.verdict === "yes" ? "Would have sent it" : "Would not have sent it"}
                  </span>
                </div>
              </section>

              {review.reasons.length > 0 && (
                <section className="rounded-xl border border-border p-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">What was wrong</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {review.reasons.map((reason) => (
                      <Badge key={reason} variant="outline" className="font-normal text-muted-foreground">
                        {REASON_LABELS[reason] ?? reason}
                      </Badge>
                    ))}
                  </div>
                </section>
              )}

              {review.otherText && (
                <section className="rounded-xl border border-border p-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Other</p>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">{review.otherText}</p>
                </section>
              )}

              {review.rewrite && (
                <section className="rounded-xl border border-border p-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    What they would send
                  </p>
                  <MarkdownContent text={review.rewrite} className="mt-2 text-sm text-foreground" />
                </section>
              )}

              <p className="px-1 text-xs text-muted-foreground">
                {review.reviewer.email ?? review.reviewer.id} · {formatTimestamp(review.createdAt)}
              </p>
            </div>
          </div>

          {onToggleAddressed && (
          <div className="shrink-0 border-t border-border px-4 py-3">
            {review.addressed ? (
              <Button
                variant="outline"
                className="w-full gap-2 text-green-foreground"
                onClick={() => onToggleAddressed(false)}
                disabled={isBusy}
              >
                <CheckCircle2 className="size-4" />
                Addressed — mark as open
              </Button>
            ) : (
              <Button className="w-full gap-2" onClick={() => onToggleAddressed(true)} disabled={isBusy}>
                <Check className="size-4" />
                Mark as addressed
              </Button>
            )}
          </div>
          )}
        </>
      )}
    </div>
  );
}
